/**
 * Modul-Level-Cache für Gmail E-Mails — überlebt Tab-Wechsel
 *
 * Ausgelagert aus useGmail.ts, um die Cache-Logik isoliert testbar zu machen.
 * updateCache unterstützt sowohl Partial-Objekte als auch funktionale Updater,
 * damit jede Aktualisierung garantiert auf dem aktuellen Stand basiert.
 */

import type { GmailMessage, GmailLoadProgress } from './useGmail';

export interface EmailCache {
  messages: GmailMessage[];
  isConnected: boolean;
  connectedEmail: string | null;
  needsReconnect: boolean;
  loading: boolean;
  loadingMessages: boolean;
  unreadCount: number;
  currentLabel: string;
  lastError: string | null;
  isPolling: boolean;
  lastSyncTime: string | null;
  loadProgress: GmailLoadProgress;
  hasMore: boolean;
  totalCount: number;
}

export const defaultCache: EmailCache = {
  messages: [],
  isConnected: false,
  connectedEmail: null,
  needsReconnect: false,
  loading: true,
  loadingMessages: false,
  unreadCount: 0,
  currentLabel: 'INBOX',
  lastError: null,
  isPolling: false,
  lastSyncTime: null,
  loadProgress: { phase: 'connecting', loaded: 0, total: 0, message: 'Verbinde...' },
  hasMore: false,
  totalCount: 0,
};

export let cache: EmailCache = { ...defaultCache };
let subscribers: Set<() => void> = new Set();

export function getCache(): EmailCache {
  return cache;
}

export function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/**
 * Cache aktualisieren — unterstützt zwei Formen:
 *
 * 1. Partial-Objekt:      updateCache({ messages: [...] })
 * 2. Funktionaler Updater: updateCache(prev => ({ messages: prev.messages.filter(...) }))
 *
 * Der funktionale Updater garantiert, dass die Aktualisierung auf dem
 * aktuellsten Cache-Stand basiert — wichtig bei schnell aufeinanderfolgenden
 * Aktionen (z.B. zwei E-Mails hintereinander archivieren).
 */
export function updateCache(partialOrUpdater: Partial<EmailCache> | ((prev: EmailCache) => Partial<EmailCache>)) {
  const partial = typeof partialOrUpdater === 'function' ? partialOrUpdater(cache) : partialOrUpdater;
  cache = { ...cache, ...partial };
  // Batch notifications to avoid excessive re-renders
  queueMicrotask(() => {
    subscribers.forEach(cb => cb());
  });
}

/** Nur für Tests — setzt den Cache auf den Ausgangszustand zurück */
export function resetCache() {
  cache = { ...defaultCache };
  subscribers = new Set();
}
